import React, { useState } from 'react';

function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    useCase: '',
    tool: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(false);

    // Use requestAnimationFrame to avoid ResizeObserver errors
    requestAnimationFrame(async () => {
      const formDataToSubmit = new FormData();
      formDataToSubmit.append('name', formData.name);
      formDataToSubmit.append('email', formData.email);
      formDataToSubmit.append('company', formData.company);
      formDataToSubmit.append('use-case', formData.useCase);
      formDataToSubmit.append('tool', formData.tool);
      formDataToSubmit.append('_replyto', formData.email);
      formDataToSubmit.append('_subject', `Layrs Demo Request from ${formData.name}`);

      try {
        const response = await fetch('https://formspree.io/f/xpwrogvr', {
          method: 'POST',
          body: formDataToSubmit,
          headers: {
            'Accept': 'application/json'
          }
        });

        if (response.ok) {
          requestAnimationFrame(() => {
            setSubmitting(false);
            setSubmitted(true);
            setFormData({
              name: '',
              email: '',
              company: '',
              useCase: '',
              tool: ''
            });

            setTimeout(() => {
              setSubmitted(false);
            }, 5000);
          });
        } else {
          throw new Error('Form submission failed');
        }
      } catch (err) {
        console.error('Form submission error:', err);
        requestAnimationFrame(() => {
          setSubmitting(false);
          setError(true);

          // Fallback to mailto
          const subject = `Layrs Demo Request: ${formData.name}`;
          const body = `Name: ${formData.name}%0AEmail: ${formData.email}%0ACompany: ${formData.company}%0AUse Case: ${formData.useCase}%0ACurrent Tool: ${formData.tool}`;
          const mailtoUrl = `mailto:vijayinjam@gmail.com?subject=${encodeURIComponent(subject)}&body=${body}`;

          setTimeout(() => {
            window.open(mailtoUrl, '_blank');
            setError(false);
          }, 2000);
        });
      }
    });
  };

  return (
    <section className="contact-section" id="contact">
      <div className="container">
        <div className="contact-container">
          <div className="contact-content">
            <span className="section-label">💬 Get in Touch</span>
            <h2 className="contact-title">Ready to Make Your Data Think?</h2>
            <p className="contact-description">
              Pick one use case. See the impact in 21 days. Scale from there.
            </p>
            <ul className="contact-benefits">
              <li>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M16.667 5L7.5 14.167 3.333 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>1-minute setup</span>
              </li>
              <li>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M16.667 5L7.5 14.167 3.333 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>White-glove onboarding</span>
              </li>
              <li>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M16.667 5L7.5 14.167 3.333 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>21-day pilot program</span>
              </li>
            </ul>
          </div>
          <div className="contact-form-wrapper">
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Full Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="John Doe"
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Work Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="john@company.com"
                />
              </div>
              <div className="form-group">
                <label htmlFor="company">Company</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  placeholder="Acme Inc."
                />
              </div>
              <div className="form-group">
                <label htmlFor="useCase">What's your primary use case?</label>
                <select
                  id="useCase"
                  name="useCase"
                  value={formData.useCase}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select one...</option>
                  <option value="answers">Quick Answers</option>
                  <option value="predict">Predict Outcomes</option>
                  <option value="explain">Explain Changes</option>
                  <option value="simulate">Simulate Scenarios</option>
                  <option value="anomalies">Detect Anomalies</option>
                  <option value="audit">Audit Decisions</option>
                  <option value="autonomous">Autonomous Actions</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Current Tool</label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="tool"
                      value="airtable"
                      checked={formData.tool === 'airtable'}
                      onChange={handleChange}
                      required
                    />
                    <span>Airtable</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="tool"
                      value="sheets"
                      checked={formData.tool === 'sheets'}
                      onChange={handleChange}
                      required
                    />
                    <span>Google Sheets</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="tool"
                      value="both"
                      checked={formData.tool === 'both'}
                      onChange={handleChange}
                      required
                    />
                    <span>Both</span>
                  </label>
                </div>
              </div>
              <button
                type="submit"
                className="btn-primary btn-full"
                disabled={submitting || submitted}
                style={submitted ? { background: '#10b981' } : error ? { background: '#ef4444' } : {}}
              >
                {submitting ? 'Submitting...' : submitted ? '✓ Request Submitted!' : error ? '⚠ Error - Try Again' : 'Start Free Pilot (21 Days)'}
              </button>
            </form>
            {submitted && (
              <p className="form-success">Thanks! We'll reach out within 24 hours.</p>
            )}
            {error && (
              <p className="form-error">Email service unavailable. Opening fallback...</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default ContactForm;
